import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LayoutDashboard, LogIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LeaseFlowMark } from "@/components/Logo";

const links = [
  { to: "/features", label: "Features" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/use-cases", label: "Use cases" },
  { to: "/customers", label: "Customers" },
  { to: "/about", label: "About" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  return (
    <header
      data-testid="site-navigation"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#060F1E]/85 backdrop-blur-xl"
          : "bg-[#060F1E]/50 backdrop-blur-md"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <LeaseFlowMark size={32} />
          <span className="font-display text-[19px] font-semibold tracking-tight text-white">
            Lease<span style={{ color: "#00D4AA" }}>Flow</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `px-3.5 py-2 text-[14px] rounded-md transition-colors ${
                  isActive ? "text-white bg-white/5" : "text-slate-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <button onClick={handleLogout} data-testid="nav-logout-btn" className="text-[14px] text-slate-400 hover:text-white">
                Sign out
              </button>
              <Link to="/app/dashboard" data-testid="nav-cta-dashboard" className="btn-primary !py-2 !px-4 !text-[13px]">
                <LayoutDashboard className="w-3.5 h-3.5" /> Dashboard
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-ghost !py-2 !px-4 !text-[13px]">
                <LogIn className="w-3.5 h-3.5" /> Log in
              </Link>
              <Link to="/demo" data-testid="nav-cta-demo" className="btn-accent !py-2 !px-4 !text-[13px]">
                Book a Demo
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-slate-200 p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#060F1E]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm rounded-md ${isActive ? "text-white bg-white/5" : "text-slate-400"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <Link to="/app/dashboard" data-testid="nav-mobile-dashboard" className="mt-2 btn-primary">
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-mobile-login" className="mt-2 btn-ghost">
                  <LogIn className="w-3.5 h-3.5" /> Log in
                </Link>
                <Link to="/demo" data-testid="nav-mobile-demo" className="mt-2 btn-accent">
                  Book a Demo
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
